/* Progressive controls for the campaign's native product rail. No commerce writes. */
if (!customElements.get('ys-halloween-product-rail')) {
  customElements.define('ys-halloween-product-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.pending) return;
      this.pending = true;
      queueMicrotask(() => {
        this.pending = false;
        if (!this.isConnected || this.controller) return;
        this.rail = this.querySelector('[data-hw-product-rail]');
        this.controls = this.querySelector('[data-hw-rail-controls]');
        this.previous = this.querySelector('[data-hw-rail-prev]');
        this.next = this.querySelector('[data-hw-rail-next]');
        this.position = this.querySelector('[data-hw-rail-position]');
        this.hint = this.querySelector('[data-hw-rail-hint]');
        if (!this.rail || !this.controls || !this.previous || !this.next || !this.position) return;
        this.items = Array.from(this.rail.children);
        if (!this.items.length) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.previous.addEventListener('click', () => this.move(-1), options);
        this.next.addEventListener('click', () => this.move(1), options);
        this.rail.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
        this.rail.addEventListener('pointerdown', event => this.startDrag(event), options);
        this.rail.addEventListener('lostpointercapture', () => this.finishDrag(), options);
        this.rail.addEventListener('dragstart', event => {
          if (this.drag) event.preventDefault();
        }, options);
        this.rail.addEventListener('click', event => {
          if (!this.suppressClick || event.detail === 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }, { ...options, capture: true });
        this.rail.addEventListener('keydown', event => {
          if (event.target !== this.rail || event.altKey || event.ctrlKey || event.metaKey) return;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') this.scrollToIndex(0);
          else if (event.key === 'End') this.scrollToIndex(this.items.length - 1);
          else this.move(event.key === 'ArrowRight' ? 1 : -1);
        }, options);
        this.rail.addEventListener('focusin', event => {
          if (this.drag) return;
          const item = event.target.closest('.ys-hw-card__item');
          if (!item || !this.rail.contains(item)) return;
          const railBox = this.rail.getBoundingClientRect();
          const itemBox = item.getBoundingClientRect();
          if (itemBox.left < railBox.left - 1 || itemBox.right > railBox.right + 1) {
            const left = itemBox.left - railBox.left + this.rail.scrollLeft;
            this.rail.scrollTo({ left, behavior: 'instant' });
          }
        }, options);
        window.addEventListener('resize', () => this.scheduleUpdate(), options);
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.scheduleUpdate());
          this.observer.observe(this.rail);
        }
        this.update();
      });
    }

    startDrag(event) {
      if (event.pointerType !== 'mouse' || !event.isPrimary || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target.closest('button, input, select, textarea, [contenteditable="true"]')) return;
      this.finishDrag();
      clearTimeout(this.clickTimer);
      this.suppressClick = false;
      if (this.rail.scrollWidth - this.rail.clientWidth <= 2) return;
      this.drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: this.rail.scrollLeft, active: false };
      this.dragController = new AbortController();
      const options = { signal: this.dragController.signal };
      // Track only this gesture before capture; never replace global pointer handlers.
      window.addEventListener('pointermove', move => this.dragMove(move), { ...options, passive: false });
      window.addEventListener('pointerup', up => {
        if (up.pointerId === this.drag?.pointerId) this.finishDrag();
      }, options);
      window.addEventListener('pointercancel', cancel => {
        if (cancel.pointerId === this.drag?.pointerId) this.finishDrag();
      }, options);
      window.addEventListener('blur', () => this.finishDrag(), options);
    }

    dragMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!(event.buttons & 1)) { this.finishDrag(); return; }
      const dx = event.clientX - drag.x;
      if (!drag.active) {
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) < 8) {
          if (Math.abs(dy) >= 8) this.finishDrag();
          return;
        }
        if (Math.abs(dy) > Math.abs(dx)) { this.finishDrag(); return; }
        drag.active = true;
        this.rail.dataset.hwDragging = 'true';
        this.rail.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      this.rail.scrollLeft = drag.left - dx;
    }

    finishDrag() {
      const drag = this.drag;
      if (!drag) return;
      this.drag = null;
      this.dragController?.abort();
      this.dragController = null;
      delete this.rail.dataset.hwDragging;
      if (this.rail.hasPointerCapture(drag.pointerId)) this.rail.releasePointerCapture(drag.pointerId);
      if (drag.active) {
        // Suppress only the mouse click generated by this drag; a new press resets it.
        this.suppressClick = true;
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
        this.scheduleUpdate();
      }
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      if (!this.isConnected || !this.rail) return;
      const overflow = this.rail.scrollWidth - this.rail.clientWidth > 2;
      this.controls.hidden = !overflow;
      if (this.hint) this.hint.hidden = true;
      const max = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      this.previous.disabled = this.rail.scrollLeft <= 2;
      this.next.disabled = this.rail.scrollLeft >= max - 2;
      const railBox = this.rail.getBoundingClientRect();
      const visible = this.items.reduce((indices, item, index) => {
        const box = item.getBoundingClientRect();
        const intersection = Math.max(0, Math.min(box.right, railBox.right) - Math.max(box.left, railBox.left));
        if (intersection >= box.width * .5) indices.push(index + 1);
        return indices;
      }, []);
      const first = visible[0] || 1;
      const last = visible[visible.length - 1] || first;
      const label = `${first === last ? first : `${first}–${last}`} / ${this.items.length}`;
      if (this.position.textContent !== label) this.position.textContent = label;
    }

    move(direction) {
      const railBox = this.rail.getBoundingClientRect();
      const positions = this.items.map(item => item.getBoundingClientRect().left - railBox.left + this.rail.scrollLeft);
      const left = this.rail.scrollLeft;
      let target;
      if (direction > 0) target = positions.findIndex(position => position > left + 4);
      else {
        for (let index = positions.length - 1; index >= 0; index -= 1) {
          if (positions[index] < left - 4) { target = index; break; }
        }
      }
      this.scrollToIndex(target == null || target < 0 ? (direction > 0 ? this.items.length - 1 : 0) : target);
    }

    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;
      const left = item.getBoundingClientRect().left - this.rail.getBoundingClientRect().left + this.rail.scrollLeft;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollTo({ left, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    disconnectedCallback() {
      this.finishDrag();
      clearTimeout(this.clickTimer);
      this.suppressClick = false;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}

